// Ícones em pixel do painel (WEB-137): a mesma língua do dither do login e da LP, no lugar do
// `lucide-react`, que é o padrão do shadcn e de meio mercado.
//
// Os nomes são os que o `lucide-react` usava, pra troca ter sido só o caminho do import. O
// desenho vem de `pixelarticons.gen.ts` (vendorado do pacote, MIT) ou de `hand.ts` (desenhado à
// mão na grade do pacote).
//
// O `apps/pay` não importa do `apps/web`: ele tem uma cópia GERADA deste arquivo, só com os
// ícones que usa (`apps/pay/src/components/icons.gen.tsx`). Por isso o trecho entre
// `PixelIconProps` e o primeiro `export const` só depende de `react`, e cada ícone é uma linha
// `export const Nome = pixel(pax.x)` ou `pixel(hand.y)`: é isso que
// `scripts/vendor-pixelarticons.ts` lê. Mudou desenho ou a fábrica? Rode o script de novo.
//
// ponytail: a grade é 24x24. Fica perfeita em 24 px, boa em 16 e 18 px (escala não inteira, o
// antialias segura em retina e fica aceitável em tela comum) e perde detalhe abaixo de 14 px.
// `shape-rendering` fica no padrão: `crispEdges` come pixel em escala não inteira. Se um uso
// abaixo de 14 px incomodar, o caminho é um desenho próprio em grade 12 pra ele, não uma flag.

import type { ReactElement, SVGProps } from 'react';
import * as hand from './hand.ts';
import * as pax from './pixelarticons.gen.ts';

export type PixelIconProps = Omit<SVGProps<SVGSVGElement>, 'ref' | 'strokeWidth'> & {
  /** Lado em px (ou qualquer comprimento CSS). 24, como no lucide. Classe `size-*` ganha. */
  size?: number | string;
  /** Aceita e ignora: em pixel não existe traço. Só pra chamada antiga continuar compilando. */
  strokeWidth?: number | string;
};

export type PixelIcon = (props: PixelIconProps) => ReactElement;

function pixel(d: string): PixelIcon {
  return function Pixel({ size = 24, strokeWidth: _strokeWidth, 'aria-label': label, ...rest }) {
    // Decorativo por default, como o lucide: quem carrega o significado é o texto ao lado. Os
    // dois ramos por extenso são pro lint de a11y do Biome (`noSvgWithoutTitle`), que lê o JSX
    // estaticamente (mesma saída do `kit/icon.tsx`).
    if (label === undefined) {
      return (
        <svg
          aria-hidden="true"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="currentColor"
          {...rest}
        >
          <path d={d} />
        </svg>
      );
    }
    return (
      <svg
        role="img"
        aria-label={label}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        {...rest}
      >
        <title>{label}</title>
        <path d={d} />
      </svg>
    );
  };
}

// Do pacote, pelo mesmo nome.
export const BookOpen = /*#__PURE__*/ pixel(pax.bookOpen);
export const ChevronDown = /*#__PURE__*/ pixel(pax.chevronDown);
export const ChevronLeft = /*#__PURE__*/ pixel(pax.chevronLeft);
export const ChevronRight = /*#__PURE__*/ pixel(pax.chevronRight);
export const ChevronUp = /*#__PURE__*/ pixel(pax.chevronUp);
export const Code = /*#__PURE__*/ pixel(pax.code);
export const Copy = /*#__PURE__*/ pixel(pax.copy);
export const Download = /*#__PURE__*/ pixel(pax.download);
export const ExternalLink = /*#__PURE__*/ pixel(pax.externalLink);
export const Image = /*#__PURE__*/ pixel(pax.image);
export const ImagePlus = /*#__PURE__*/ pixel(pax.imagePlus);
export const Inbox = /*#__PURE__*/ pixel(pax.inbox);
export const List = /*#__PURE__*/ pixel(pax.list);
export const Lock = /*#__PURE__*/ pixel(pax.lock);
export const Menu = /*#__PURE__*/ pixel(pax.menu);
export const Monitor = /*#__PURE__*/ pixel(pax.monitor);
export const Moon = /*#__PURE__*/ pixel(pax.moon);
export const MoreHorizontal = /*#__PURE__*/ pixel(pax.moreHorizontal);
export const Percent = /*#__PURE__*/ pixel(pax.percent);
export const Plus = /*#__PURE__*/ pixel(pax.plus);
export const Search = /*#__PURE__*/ pixel(pax.search);
export const ShoppingBag = /*#__PURE__*/ pixel(pax.shoppingBag);
export const Store = /*#__PURE__*/ pixel(pax.store);
export const Upload = /*#__PURE__*/ pixel(pax.upload);
export const User = /*#__PURE__*/ pixel(pax.user);
export const UserPlus = /*#__PURE__*/ pixel(pax.userPlus);
export const Users = /*#__PURE__*/ pixel(pax.users);
export const Wallet = /*#__PURE__*/ pixel(pax.wallet);
export const Zap = /*#__PURE__*/ pixel(pax.zap);

// Do pacote, por apelido: o nome de lá é outro, o desenho é o mesmo objeto.
export const ArrowDownToLine = /*#__PURE__*/ pixel(pax.arrowBarDown);
export const ArrowLeftRight = /*#__PURE__*/ pixel(pax.sync);
export const Banknote = /*#__PURE__*/ pixel(pax.money);
export const Bell = /*#__PURE__*/ pixel(pax.notification);
export const BookText = /*#__PURE__*/ pixel(pax.book);
export const ChartLine = /*#__PURE__*/ pixel(pax.trending);
// "Comece aqui" na documentação: bandeira de largada. O alvo (`bullseye-arrow`) lê como meta.
export const Compass = /*#__PURE__*/ pixel(pax.flag);
export const ChevronsUpDown = /*#__PURE__*/ pixel(pax.chevronsVertical);
export const FileText = /*#__PURE__*/ pixel(pax.fileAlt);
export const Gauge = /*#__PURE__*/ pixel(pax.speedMedium);
export const LayoutGrid = /*#__PURE__*/ pixel(pax.dashboard);
export const Link2 = /*#__PURE__*/ pixel(pax.link);
export const LoaderCircle = /*#__PURE__*/ pixel(pax.loader);
export const LogOut = /*#__PURE__*/ pixel(pax.logout);
export const Pencil = /*#__PURE__*/ pixel(pax.edit);
export const Receipt = /*#__PURE__*/ pixel(pax.reciept);
export const ReceiptText = /*#__PURE__*/ pixel(pax.recieptAlt);
export const Repeat2 = /*#__PURE__*/ pixel(pax.reload);
export const RotateCw = /*#__PURE__*/ pixel(pax.repeat);
export const Settings = /*#__PURE__*/ pixel(pax.sliders);
export const Sun = /*#__PURE__*/ pixel(pax.sunAlt);
export const Trash2 = /*#__PURE__*/ pixel(pax.trash);
export const WalletCards = /*#__PURE__*/ pixel(pax.creditCardMultiple);
export const WifiOff = /*#__PURE__*/ pixel(pax.cellularSignalOff);

// Desenhados à mão: o pacote não tem (ou o de mesmo nome é outro objeto, caso do `scale`).
export const ArrowUpFromLine = /*#__PURE__*/ pixel(hand.arrowUpFromLine);
export const ArrowUpRight = /*#__PURE__*/ pixel(hand.arrowUpRight);
// Check e X gordos (decisão do Caio na folha): os do pacote ficam leves em 16 px.
export const Check = /*#__PURE__*/ pixel(hand.checkBold);
export const CircleAlert = /*#__PURE__*/ pixel(hand.circleAlert);
export const CircleCheck = /*#__PURE__*/ pixel(hand.circleCheck);
export const CircleIcon = /*#__PURE__*/ pixel(hand.dot);
export const Cpu = /*#__PURE__*/ pixel(hand.cpu);
export const FileUp = /*#__PURE__*/ pixel(hand.fileUp);
export const FlaskConical = /*#__PURE__*/ pixel(hand.flask);
export const Info = /*#__PURE__*/ pixel(hand.circleInfo);
export const KeyRound = /*#__PURE__*/ pixel(hand.key);
export const MailPlus = /*#__PURE__*/ pixel(hand.mailPlus);
export const Package = /*#__PURE__*/ pixel(hand.cube);
export const Plug = /*#__PURE__*/ pixel(hand.plug);
export const Scale = /*#__PURE__*/ pixel(hand.scale);
export const ScanFace = /*#__PURE__*/ pixel(hand.scanFace);
export const SearchX = /*#__PURE__*/ pixel(hand.searchX);
export const Send = /*#__PURE__*/ pixel(hand.send);
export const Settings2 = /*#__PURE__*/ pixel(hand.sliders2);
export const ShieldCheck = /*#__PURE__*/ pixel(hand.shieldCheck);
export const Split = /*#__PURE__*/ pixel(hand.split);
export const TriangleAlert = /*#__PURE__*/ pixel(hand.triangleAlert);
export const Webhook = /*#__PURE__*/ pixel(hand.webhook);
export const X = /*#__PURE__*/ pixel(hand.closeBold);

// Nomes duplicados que o lucide também exporta (o shadcn importa a forma com `Icon`).
export const AlertTriangle = TriangleAlert;
export const CheckIcon = Check;
export const ChevronDownIcon = ChevronDown;
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRightIcon = ChevronRight;
export const ChevronUpIcon = ChevronUp;
export const Code2 = Code;
export const Key = KeyRound;
export const SearchIcon = Search;
export const XIcon = X;
